import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline'
import { useEffect, useState } from 'react'
import { Modal } from 'stories/components'

import { type IAccountsReceivable, type IAccountsReceivableSettings, type IArLine, emptyLine } from './types'

const inputClass = 'border border-gray-300 rounded px-2 py-1.5 w-full text-sm'

const today = () => new Date().toISOString().slice(0, 10)

const money = (value: number) =>
  `$${(Number(value) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

const blank = (settings?: IAccountsReceivableSettings | null): IAccountsReceivable => ({
  id: 0,
  invoiceNo: '',
  invoiceDate: today(),
  dueDate: null,
  dueLabel: 'Upon Receipt / At Closing',
  billToName: '',
  billToAddress: '',
  billToEmail: '',
  subjectProperty: '',
  lines: [emptyLine()],
  amount: 0,
  paymentNote: settings?.paymentNote || '',
  isPaid: false,
  paidAt: null,
  paidNotifyEmails: '',
  reminderMode: 'global',
  reminderDaysBefore: settings?.daysBefore || [7, 1, 0],
  reminderOverdueEvery: settings?.overdueEveryDays ?? 7,
  reminderEmails: '',
  lastReminderOn: null,
  createdAt: '',
})

export const AccountsReceivableModal = ({
  item,
  settings,
  onClose,
  onSave,
}: {
  item: IAccountsReceivable | null
  settings: IAccountsReceivableSettings | null
  onClose: () => void
  onSave: (data: Record<string, any>) => Promise<void>
}) => {
  const [form, setForm] = useState<IAccountsReceivable>(blank(settings))
  const [daysText, setDaysText] = useState('7, 1, 0')
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    const next = item ? { ...item, lines: item.lines?.length ? item.lines : [emptyLine()] } : blank(settings)
    setForm(next)
    setDaysText((next.reminderDaysBefore || []).join(', '))
    setError('')
  }, [item, settings])

  const set = (patch: Partial<IAccountsReceivable>) => setForm((prev) => ({ ...prev, ...patch }))

  const setLine = (index: number, patch: Partial<IArLine>) => {
    const lines = form.lines.map((line, i) => (i === index ? { ...line, ...patch } : line))
    set({ lines })
  }

  const total = form.lines.reduce((sum, line) => sum + (Number(line.amount) || 0), 0)

  const onSubmit = async () => {
    if (!form.billToName.trim()) {
      setError('Bill to is required.')
      return false
    }
    if (!form.invoiceDate) {
      setError('Invoice date is required.')
      return false
    }
    if (!form.lines.some((line) => line.description.trim())) {
      setError('Add at least one line with a description.')
      return false
    }
    setError('')
    setSaving(true)
    try {
      await onSave({
        invoiceNo: form.invoiceNo.trim(),
        invoiceDate: form.invoiceDate,
        dueDate: form.dueDate || null,
        dueLabel: form.dueLabel,
        billToName: form.billToName,
        billToAddress: form.billToAddress,
        billToEmail: form.billToEmail,
        subjectProperty: form.subjectProperty,
        lines: form.lines.map((line) => ({
          description: line.description,
          detail: line.detail,
          loanAmount: null,
          rate: line.rate === null || line.rate === ('' as any) ? null : Number(line.rate),
          amount: Number(line.amount) || 0,
        })),
        paymentNote: form.paymentNote,
        isPaid: form.isPaid,
        paidNotifyEmails: form.paidNotifyEmails,
        reminderMode: form.reminderMode,
        reminderDaysBefore: daysText
          .split(/[, ]+/)
          .map((part) => Number(part))
          .filter((part) => Number.isFinite(part) && part >= 0),
        reminderOverdueEvery: Number(form.reminderOverdueEvery) || 0,
        reminderEmails: form.reminderEmails,
      })
      return true
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      title={item ? `Invoice ${item.invoiceNo}` : 'New Invoice'}
      titleOkay="Save"
      loading={saving}
      isOpen
      onClose={onClose}
      onOk={onSubmit}
      childLevel={1}
    >
      <div className="w-[760px] max-w-full text-sm grid grid-cols-2 gap-3">
        {error && <p className="col-span-2 text-red-700">{error}</p>}
        <label>
          <span className="text-gray-600">Invoice #</span>
          <input
            className={`${inputClass} mt-1`}
            placeholder={settings?.nextInvoiceNo || 'Assigned on save'}
            value={form.invoiceNo}
            onChange={(e) => set({ invoiceNo: e.target.value })}
          />
        </label>
        <label>
          <span className="text-gray-600">Invoice date</span>
          <input
            type="date"
            className={`${inputClass} mt-1`}
            value={String(form.invoiceDate || '').slice(0, 10)}
            onChange={(e) => set({ invoiceDate: e.target.value })}
          />
        </label>
        <label>
          <span className="text-gray-600">Due date</span>
          <input
            type="date"
            className={`${inputClass} mt-1`}
            value={form.dueDate ? String(form.dueDate).slice(0, 10) : ''}
            onChange={(e) => set({ dueDate: e.target.value || null })}
          />
        </label>
        <label>
          <span className="text-gray-600">Due (printed)</span>
          <input
            className={`${inputClass} mt-1`}
            value={form.dueLabel}
            onChange={(e) => set({ dueLabel: e.target.value })}
          />
        </label>
        <label>
          <span className="text-gray-600">Bill to</span>
          <input
            className={`${inputClass} mt-1`}
            value={form.billToName}
            onChange={(e) => set({ billToName: e.target.value })}
          />
        </label>
        <label>
          <span className="text-gray-600">Bill-to email</span>
          <input
            className={`${inputClass} mt-1`}
            value={form.billToEmail}
            onChange={(e) => set({ billToEmail: e.target.value })}
          />
        </label>
        <label className="col-span-2">
          <span className="text-gray-600">Bill-to address</span>
          <textarea
            className={`${inputClass} mt-1`}
            rows={2}
            value={form.billToAddress}
            onChange={(e) => set({ billToAddress: e.target.value })}
          />
        </label>
        <div className="col-span-2">
          <div className="flex items-center justify-between mb-1">
            <span className="text-gray-600">Lines</span>
            <button
              type="button"
              className="text-shade-blue flex items-center gap-1"
              onClick={() => set({ lines: [...form.lines, emptyLine()] })}
            >
              <PlusIcon className="w-4 h-4" /> Add line
            </button>
          </div>
          <div className="border rounded overflow-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-500">
                <tr>
                  <th className="text-left px-2 py-1">Description</th>
                  <th className="text-right px-2 py-1 w-28">Rate $</th>
                  <th className="text-right px-2 py-1 w-28">Amount</th>
                  <th className="w-8" />
                </tr>
              </thead>
              <tbody>
                {form.lines.map((line, index) => (
                  <tr key={index} className="border-t">
                    <td className="p-1">
                      <input
                        className={inputClass}
                        value={line.description}
                        onChange={(e) => setLine(index, { description: e.target.value })}
                      />
                      <input
                        className={`${inputClass} mt-1`}
                        value={line.detail}
                        placeholder="Property or loan note"
                        onChange={(e) => setLine(index, { detail: e.target.value })}
                      />
                    </td>
                    <td className="p-1 align-top">
                      <div className="relative">
                        <span className="absolute left-2 top-1.5 text-gray-500">$</span>
                        <input
                          className={`${inputClass} text-right pl-5`}
                          value={line.rate ?? ''}
                          onChange={(e) =>
                            setLine(index, { rate: e.target.value === '' ? null : Number(e.target.value) })
                          }
                        />
                      </div>
                    </td>
                    <td className="p-1 align-top">
                      <input
                        className={`${inputClass} text-right`}
                        value={line.amount || ''}
                        onChange={(e) => setLine(index, { amount: Number(e.target.value) || 0 })}
                      />
                    </td>
                    <td className="p-1 align-top">
                      <button
                        type="button"
                        className="text-red-700 p-1"
                        onClick={() => set({ lines: form.lines.filter((_, i) => i !== index) })}
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-right font-semibold mt-2">Total due {money(total)}</p>
        </div>

        <label className="col-span-2">
          <span className="text-gray-600">Payment instructions</span>
          <textarea
            className={`${inputClass} mt-1`}
            rows={3}
            value={form.paymentNote}
            onChange={(e) => set({ paymentNote: e.target.value })}
          />
        </label>

        <label>
          <span className="text-gray-600">Unpaid reminders</span>
          <select
            className={`${inputClass} mt-1`}
            value={form.reminderMode}
            onChange={(e) => set({ reminderMode: e.target.value as IAccountsReceivable['reminderMode'] })}
          >
            <option value="global">Use the global schedule</option>
            <option value="custom">Custom schedule for this invoice</option>
            <option value="off">Do not remind</option>
          </select>
        </label>
        <label className="flex items-end gap-2 pb-2">
          <input type="checkbox" checked={form.isPaid} onChange={(e) => set({ isPaid: e.target.checked })} />
          <span>Paid</span>
        </label>
        {form.reminderMode === 'custom' && (
          <>
            <label>
              <span className="text-gray-600">Days before due (comma separated, 0 = due date)</span>
              <input className={`${inputClass} mt-1`} value={daysText} onChange={(e) => setDaysText(e.target.value)} />
            </label>
            <label>
              <span className="text-gray-600">Then every N days overdue</span>
              <input
                type="number"
                min={0}
                className={`${inputClass} mt-1`}
                value={form.reminderOverdueEvery}
                onChange={(e) => set({ reminderOverdueEvery: Number(e.target.value) || 0 })}
              />
            </label>
            <label className="col-span-2">
              <span className="text-gray-600">Reminder emails for this invoice</span>
              <input
                className={`${inputClass} mt-1`}
                placeholder="name@company.com, other@company.com"
                value={form.reminderEmails}
                onChange={(e) => set({ reminderEmails: e.target.value })}
              />
            </label>
          </>
        )}
        <label className="col-span-2">
          <span className="text-gray-600">Also email these addresses when this invoice is paid</span>
          <input
            className={`${inputClass} mt-1`}
            value={form.paidNotifyEmails}
            onChange={(e) => set({ paidNotifyEmails: e.target.value })}
          />
        </label>
        <p className="col-span-2 text-gray-500">
          A due date is required for automatic reminders. Marking the invoice paid sends the paid notice.
        </p>
      </div>
    </Modal>
  )
}
